import { FC, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import MakerHubArticleCard from '~/components/MakerHubArticleCard/MakerHubArticleCard'
import { NoMakerHubArticlesFound } from '~/components/NoMakerHubArticlesFound/NoMakerHubArticlesFound'
import { ProductListFilters } from '~/components/ProductListFilters/ProductListFilters'
import RichText from '~/components/RichText/RichText'
import { SectionContainer } from '~/components/SectionContainer'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import {
  FilterProvider,
  useFilterContext,
} from '~/providers/FilterProvider/FilterProvider'
import {
  PageType,
  PaginationProvider,
  usePaginationContext,
  PaginationProviderInfo,
} from '~/providers/PaginationProvider/PaginationProviderGlobal'
import styles from './MakerHubArticleListing.module.css'
import {
  ArticleList,
  MakerHubArticleListingCoreProps,
  MakerHubArticleListingProps,
} from './MakerHubArticleListing.types'

export const MakerHubArticleListing: FC<MakerHubArticleListingCoreProps> = (
  props
) => {
  const {
    headerText,
    headerBody,
    articlesList: initialArticleList,
    width,
    sort,
  } = props
  const {
    searchType,
    searchQuery,
    searchFilters,
    selectedFilterMap,
    selectedSortOption,
  } = useFilterContext()

  const { pagination, setPagination, renderNextPagination } =
    usePaginationContext()

  const [articles, setArticles] = useState<ArticleList>(initialArticleList)
  const [prevArticleList, setPrevArticleList] =
    useState<ArticleList>(initialArticleList)

  useEffect(() => {
    if (initialArticleList !== prevArticleList) {
      setPrevArticleList(initialArticleList)
      setArticles(initialArticleList)
      setPagination((prev) => {
        return {
          ...prev,
          currentPage: pagination.currentPage || 1,
        }
      })
    }
  }, [initialArticleList, pagination, prevArticleList, setPagination])

  return (
    <SectionContainer width={width}>
      <div className={styles.root}>
        <div className={classNames(styles.headerSection, 'pb-6')}>
          <Typography
            className={classNames('pb-4 md:pb-2')}
            tag={TypographyTag.h1}
            variant={TypographyVariant.Heading5}>
            {headerText}
          </Typography>
          <RichText content={headerBody} />
        </div>
        <div className={classNames(styles.filters, 'flex items-center pb-6')}>
          <ProductListFilters
            search
            title='Explore by'
            sort={sort}
            searchPlaceholder='Search for an article'
            setPagination={setPagination}
          />
        </div>

        <div
          className={classNames(
            'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-4'
          )}>
          {articles?.length > 0 ? (
            articles?.map((article, index) => {
              return <MakerHubArticleCard key={index} {...article} />
            })
          ) : (
            <NoMakerHubArticlesFound />
          )}
        </div>
        <div className='flex justify-center pb-20 mt-10'>
          {renderNextPagination({
            productCnt: articles.length,
            filterInfo: {
              searchType,
              searchQuery,
              searchFilters,
              selectedSortOption,
              selectedFilterMap,
              setArticles,
            },
          })}
        </div>
      </div>
    </SectionContainer>
  )
}

const MakerHubArticleListingWithPagination: FC<
  MakerHubArticleListingCoreProps & PaginationProviderInfo
> = (props) => {
  const { totalPages, currentPage, totalProducts, ..._props } = props

  const values = useMemo(() => {
    return {
      pageType: 'Articles' as PageType,
      totalPages,
      currentPage,
      totalProducts,
    }
  }, [currentPage, totalPages, totalProducts])

  return (
    <PaginationProvider {...values}>
      <MakerHubArticleListing {..._props} />
    </PaginationProvider>
  )
}

export const MakerHubArticleListingWrapper: FC<MakerHubArticleListingProps> = (
  props
) => {
  const { selectedFilters, filters, searchType, selectedSort, ..._props } =
    props

  return (
    <FilterProvider
      filters={filters}
      searchType={searchType}
      selectedFilters={selectedFilters}
      selectedSort={selectedSort}>
      <MakerHubArticleListingWithPagination {..._props} />
    </FilterProvider>
  )
}

export default MakerHubArticleListingWrapper
